import { apiClient } from './apiClient';
import { debugLogger } from '../utils/debugLogger';

// Mood types
export type Mood = 'Anger' | 'Disgust' | 'Fear' | 'Sadness' | 'Enjoyment' | 'Other' | 'Surprise';

// Topic types
export enum Topic {
  CHINH_TRI_XA_HOI = 'chinh_tri_xa_hoi',
  DOI_SONG = 'doi_song',
  KHOA_HOC = 'khoa_hoc',
  KINH_DOANH = 'kinh_doanh',
  PHAP_LUAT = 'phap_luat',
  SUC_KHOE = 'suc_khoe',
  THE_GIOI = 'the_gioi',
  THE_THAO = 'the_thao',
  VAN_HOA = 'van_hoa',
  VI_TINH = 'vi_tinh'
}

// Moderation types
export enum PostModerationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

// Types for Posts
export interface Media {
  id: number;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  createdAt: string;
  classificationLabel?: string;
  classificationConfidence?: number;
}

export interface Post {
  id: number;
  authorId: number;
  authorUsername: string;
  authorDisplayName: string;
  authorAvatarUrl?: string | null;
  authorBadges?: Array<{
    badgeId: number;
    badgeType: string;
    badgeName: string;
    badgeIconUrl: string;
  }>;
  groupId?: number | null;
  groupName?: string | null;
  content: string;
  visibility: 'public_' | 'friends' | 'private' | 'private_' | 'group';
  commentCount: number;
  reactionCount: number;
  shareCount: number;
  reactions?: {
    totalCount: number;
    reactionCounts: Array<{
      type: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';
      count: number;
    }>;
    recentReactions?: Array<{
      id: number;
      type: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';
      userId: number;
      username: string;
      displayName: string;
      avatarUrl?: string | null;
      createdAt: string;
    }>;
    currentUserReacted: boolean;
    currentUserReactionType?: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry' | null;
  };
  createdAt: string;
  updatedAt: string;
  userReaction?: string | null;
  images?: string[];
  media?: Media[]; // Add media property to support images and videos
  mood?: string;
  moodScore?: number;
  topic?: Topic | string;
  topicConfidence?: number;
  moderationStatus?: PostModerationStatus;
  moderationReason?: string | null;
  moderatedBy?: number | null;
  moderatedAt?: string | null;
  isSensitive?: boolean;
  isViolent?: boolean;
  
  // Share info - for shared posts
  shareInfo?: {
    shareId: number;
    sharedByUserId: number;
    sharedByUsername: string;
    sharedByDisplayName: string;
    sharedByAvatarUrl?: string | null;
    sharedByBadges?: import('../types/badge').Badge[];
    shareComment?: string;
    sharedAt: string;
  };
  sharedPost?: Post; // The original post if this is a share
  
  // Legacy support - computed properties for backward compatibility
  author?: {
    id: number;
    username: string;
    displayName: string;
    avatarUrl?: string | null;
    isVerified?: boolean;
    badges?: import('../types/badge').Badge[];
  };
  reactionsCount?: {
    like: number;
    love: number;
    wow: number;
    sad: number;
    angry: number;
    haha: number;
    total: number;
  };
  commentsCount?: number;
  sharesCount?: number;
}

export interface CreatePostRequest {
  content: string;
  visibility: 'public_' | 'friends' | 'private_' | 'group';
  groupId?: number | null;
  mediaUrls?: string[];
  mood?: Mood;
}

export interface UpdatePostRequest {
  content?: string;
  visibility?: 'public_' | 'friends' | 'private_' | 'group';
  mediaUrls?: string[];
}

export interface PostReactionRequest {
  type: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';
}

export interface SharePostRequest {
  comment?: string;
  visibility?: 'public_' | 'friends' | 'private_' | 'group';
}

export interface PostsResponse {
  content: Post[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
  numberOfElements: number;
  pageable?: any;
  sort?: any;
}

class PostService {
  private baseUrl = '/api/posts';

  // Create a new post with JSON body and mediaUrls
  async createPost(postData: CreatePostRequest): Promise<Post> {
    debugLogger.logApiCall('POST', this.baseUrl, postData);
    try {
      const payload: any = {
        content: postData.content,
        visibility: postData.visibility,
        mediaUrls: postData.mediaUrls || []
      }
      if (postData.groupId) payload.groupId = postData.groupId
      if (postData.mood) payload.mood = postData.mood
      const response = await apiClient.post<Post>(this.baseUrl, payload)
      debugLogger.logApiResponse('POST', this.baseUrl, response);
      return response;
    } catch (error) {
      debugLogger.logApiResponse('POST', this.baseUrl, null, error);
      throw error;
    }
  }

  // Removed with-media variant (deprecated)

  // Get a specific post
  async getPost(postId: number): Promise<Post> {
    return await apiClient.get<Post>(`${this.baseUrl}/${postId}`);
  }

  // Update a post with JSON
  async updatePost(postId: number, updateData: UpdatePostRequest): Promise<Post> {
    debugLogger.logApiCall('PUT', `${this.baseUrl}/${postId}`, updateData);
    try {
      const payload: any = {}
      if (updateData.content !== undefined) payload.content = updateData.content
      if (updateData.visibility !== undefined) payload.visibility = updateData.visibility
      if (updateData.mediaUrls !== undefined) payload.mediaUrls = updateData.mediaUrls
      const response = await apiClient.put<Post>(`${this.baseUrl}/${postId}`, payload)
      debugLogger.logApiResponse('PUT', `${this.baseUrl}/${postId}`, response);
      return response;
    } catch (error) {
      debugLogger.logApiResponse('PUT', `${this.baseUrl}/${postId}`, null, error);
      throw error;
    }
  }

  // Removed update with-media variant (deprecated)

  // Delete a post
  async deletePost(postId: number): Promise<void> {
    const endpoint = `${this.baseUrl}/${postId}`;
    debugLogger.logApiCall('DELETE', endpoint, { postId });
    try {
      await apiClient.delete<void>(endpoint);
      debugLogger.logApiResponse('DELETE', endpoint, { success: true });
      debugLogger.log('PostService', `🗑️ Post deleted successfully`, {
        endpoint,
        postId
      });
    } catch (error) {
      debugLogger.logApiResponse('DELETE', endpoint, null, error);
      debugLogger.log('PostService', `❌ Delete Post API Error`, {
        endpoint,
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getFeedPosts(page = 0, size = 5): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/feed/user`;
    debugLogger.logApiCall('GET', endpoint, { page, size });
    try {
      const response = await apiClient.get<any>(endpoint, {
        params: { page, size }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', 'Personalized feed loaded', {
        postsCount: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      });
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  // List posts by author
  async getPostsByAuthor(authorId: number, page = 0, size = 5): Promise<PostsResponse> {
    const endpoint = this.baseUrl;
    debugLogger.logApiCall('GET', endpoint, { authorId, page, size });
    try {
      const response = await apiClient.get<PostsResponse>(endpoint, {
        params: { authorId, page, size }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      debugLogger.log('PostService', 'Posts by author loaded', {
        authorId,
        postsCount: response.content?.length || 0,
        firstPostReactionInfo: response.content?.[0] ? {
          postId: response.content[0].id,
          userReaction: response.content[0].userReaction,
          hasReactionsObj: !!response.content[0].reactions,
          currentUserReacted: response.content[0].reactions?.currentUserReacted,
          currentUserReactionType: response.content[0].reactions?.currentUserReactionType
        } : null
      });
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  // Get all posts (simulating news feed since no specific feed endpoint exists)
  async getAllPosts(page = 0, size = 5): Promise<PostsResponse> {
    return await apiClient.get<PostsResponse>(this.baseUrl, {
      params: { page, size }
    });
  }

  // Get news feed posts using dedicated feed endpoint
  async getNewsFeed(page = 0, size = 5, sort = 'createdAt,desc'): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/feed`;
    debugLogger.logApiCall('GET', endpoint, { page, size, sort });
    try {
      const response = await apiClient.get<PostsResponse>(endpoint, {
        params: { page, size, sort }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      
      // Enhanced debug logging
      debugLogger.log('PostService', `📰 News Feed API Response`, {
        endpoint,
        page,
        size,
        sort,
        totalPosts: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        currentPage: response.number,
        hasNext: response.number < response.totalPages - 1,
        posts: response.content?.map(p => ({
          id: p.id,
          author: p.authorUsername,
          displayName: p.authorDisplayName,
          content: p.content?.substring(0, 50) + '...',
          reactions: p.reactions?.totalCount || 0,
          comments: p.commentCount || 0,
          shares: p.shareCount || 0,
          visibility: p.visibility,
          createdAt: p.createdAt,
          userReaction: p.userReaction,
          currentUserReacted: p.reactions?.currentUserReacted,
          currentUserReactionType: p.reactions?.currentUserReactionType,
          recentReactionsCount: p.reactions?.recentReactions?.length || 0
        }))
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ News Feed API Error`, {
        endpoint,
        page,
        size,
        sort,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPublicPosts(page = 0, size = 5, sort = 'createdAt,desc'): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/feed`;
    debugLogger.logApiCall('GET', endpoint, { page, size, sort });
    try {
      const response = await apiClient.get<any>(endpoint, {
        params: { page, size, sort }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', `🌍 Public Posts API Response`, {
        endpoint,
        page,
        size,
        sort,
        totalPosts: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        currentPage: response.number,
        hasNext: response.number < response.totalPages - 1,
        posts: response.content?.map((p: Post) => ({
          id: p.id,
          author: p.authorUsername,
          displayName: p.authorDisplayName,
          content: p.content?.substring(0, 50) + '...',
          reactions: p.reactions?.totalCount || 0,
          comments: p.commentCount || 0,
          shares: p.shareCount || 0,
          visibility: p.visibility,
          createdAt: p.createdAt
        }))
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ Public Posts API Error`, {
        endpoint,
        page,
        size,
        sort,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Fallback to getAllPosts with public filter if public endpoint doesn't exist
      try {
        const fallbackResponse = await this.getAllPosts(page, size);
        // Filter only public posts
        const publicPosts = {
          ...fallbackResponse,
          content: fallbackResponse.content.filter(post => post.visibility === 'public_')
        };
        debugLogger.log('PostService', `🔄 Fallback to filtered public posts`, {
          originalCount: fallbackResponse.content.length,
          publicCount: publicPosts.content.length
        });
        return publicPosts;
      } catch (fallbackError) {
        debugLogger.log('PostService', `❌ Fallback also failed`, {
          error: fallbackError instanceof Error ? fallbackError.message : 'Unknown error'
        });
        throw error; // Throw original error
      }
    }
  }

  // React to a post
  async reactToPost(postId: number, reactionData: PostReactionRequest): Promise<void> {
    const endpoint = `${this.baseUrl}/${postId}/reactions`;
    debugLogger.logApiCall('POST', endpoint, reactionData);
    try {
      const response = await apiClient.post(endpoint, reactionData);
      debugLogger.logApiResponse('POST', endpoint, response);
      
      debugLogger.log('PostService', `👍 React to Post API Success`, {
        endpoint,
        postId,
        reactionType: reactionData.type,
        response: response
      });
    } catch (error) {
      debugLogger.logApiResponse('POST', endpoint, null, error);
      debugLogger.log('PostService', `❌ React to Post API Error`, {
        endpoint,
        postId,
        reactionType: reactionData.type,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Remove reaction from post
  async unreactPost(postId: number): Promise<void> {
    const endpoint = `${this.baseUrl}/${postId}/reactions`;
    debugLogger.logApiCall('DELETE', endpoint);
    try {
      const response = await apiClient.delete(endpoint);
      debugLogger.logApiResponse('DELETE', endpoint, response);
      
      debugLogger.log('PostService', `👎 Unreact Post API Success`, {
        endpoint,
        postId,
        response: response
      });
    } catch (error) {
      debugLogger.logApiResponse('DELETE', endpoint, null, error);
      debugLogger.log('PostService', `❌ Unreact Post API Error`, {
        endpoint,
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Share a post with optional comment and visibility
  async sharePost(postId: number, shareData: SharePostRequest): Promise<number> {
    debugLogger.logApiCall('POST', `${this.baseUrl}/${postId}/shares`, shareData);
    try {
      const response = await apiClient.post<number>(`${this.baseUrl}/${postId}/shares`, shareData);
      debugLogger.logApiResponse('POST', `${this.baseUrl}/${postId}/shares`, response);
      return response;
    } catch (error) {
      debugLogger.logApiResponse('POST', `${this.baseUrl}/${postId}/shares`, null, error);
      throw error;
    }
  }

  // Unshare a post (delete share)
  async unsharePost(shareId: number): Promise<void> {
    const endpoint = `${this.baseUrl}/shares/${shareId}`;
    debugLogger.logApiCall('DELETE', endpoint, { shareId });
    try {
      await apiClient.delete<void>(endpoint);
      debugLogger.logApiResponse('DELETE', endpoint, { success: true });
    } catch (error) {
      debugLogger.logApiResponse('DELETE', endpoint, null, error);
      throw error;
    }
  }

  // Get feed with shared posts (includes both original and shared posts)
  async getFeedWithShares(page = 0, size = 20): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/feed/with-shares`;
    debugLogger.logApiCall('GET', endpoint, { page, size });
    
    const timestamp = Date.now();
    console.log('🔍 getFeedWithShares called with:', {
      page,
      size,
      timestamp,
      fullUrl: `${endpoint}?page=${page}&size=${size}&_t=${timestamp}`
    });
    
    try {
      const response = await apiClient.get<PostsResponse>(endpoint, {
        params: { 
          page, 
          size,
          _t: timestamp // Cache buster
        }
      });
      
      console.log('🔍 REQUEST PARAMS SENT:', {
        endpoint,
        params: { page, size, _t: timestamp },
        baseURL: process.env.REACT_APP_API_URL
      });
      
      console.log('🔍 RAW RESPONSE FROM APICLIENT:', {
        type: typeof response,
        isArray: Array.isArray(response),
        keys: Object.keys(response),
        hasContent: 'content' in response,
        hasTotalPages: 'totalPages' in response,
        rawResponse: response
      });
      
      console.log('📊 getFeedWithShares response pagination:', {
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        currentPage: response.number,
        pageSize: response.size,
        numberOfElements: response.numberOfElements,
        first: response.first,
        last: response.last,
        calculation: `${response.totalElements} / ${response.size} = ${Math.ceil(response.totalElements / response.size)} pages`
      });
      
      // Warn if pagination looks suspicious
      if (response.totalElements > response.size && response.totalPages === 1) {
        console.warn('⚠️ PAGINATION MISMATCH DETECTED!', {
          issue: 'Backend returned totalPages=1 but has more elements than page size',
          totalElements: response.totalElements,
          pageSize: response.size,
          totalPages: response.totalPages,
          expectedPages: Math.ceil(response.totalElements / response.size),
          suggestion: 'Check backend pagination implementation in /api/posts/feed/with-shares'
        });
      }
      
      debugLogger.logApiResponse('GET', endpoint, response);
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  async getPostReactions(postId: number, page = 0, size = 5): Promise<{
    content: Array<{
      id: number;
      type: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';
      userId: number;
      username: string;
      displayName: string;
      avatarUrl?: string | null;
      createdAt: string;
    }>;
    totalElements: number;
  }> {
    const endpoint = `${this.baseUrl}/${postId}/reactions`;
    debugLogger.logApiCall('GET', endpoint, { postId, page, size });
    try {
      const response = await apiClient.get<{
        content: Array<{
          id: number;
          type: 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';
          userId: number;
          username: string;
          displayName: string;
          avatarUrl?: string | null;
          createdAt: string;
        }>;
        totalElements: number;
      }>(endpoint, {
        params: { page, size }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', `👥 Get Post Reactions API Success`, {
        endpoint,
        postId,
        page,
        size,
        totalReactions: response.totalElements,
        reactionsCount: response.content?.length || 0,
        reactions: response.content?.map(r => ({
          id: r.id,
          type: r.type,
          userId: r.userId,
          username: r.username,
          displayName: r.displayName,
          createdAt: r.createdAt
        }))
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ Get Post Reactions API Error`, {
        endpoint,
        postId,
        page,
        size,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPostsByMood(mood: Mood, page?: number, size?: number): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/mood/${mood}`;
    debugLogger.logApiCall('GET', endpoint, { mood, page, size });
    
    try {
      const params: any = {};
      if (page !== undefined) params.page = page;
      if (size !== undefined) params.size = size;

      const response = await apiClient.get<any>(endpoint, {
        params
      });
      
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', `📊 Posts by Mood API Response`, {
        endpoint,
        mood,
        page: page ?? 'all',
        size: size ?? 'all',
        totalPosts: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ Get Posts by Mood API Error`, {
        endpoint,
        mood,
        page,
        size,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPostsByTopic(topic: Topic | string, page?: number, size?: number): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/topic/${topic}`;
    debugLogger.logApiCall('GET', endpoint, { topic, page, size });
    
    try {
      const params: any = {};
      if (page !== undefined) params.page = page;
      if (size !== undefined) params.size = size;

      const response = await apiClient.get<any>(endpoint, {
        params
      });
      
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', `🏷️ Posts by Topic API Response`, {
        endpoint,
        topic,
        page: page ?? 'all',
        size: size ?? 'all',
        totalPosts: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ Get Posts by Topic API Error`, {
        endpoint,
        topic,
        page,
        size,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPersonalizedFeed(page?: number, size?: number): Promise<PostsResponse> {
    const endpoint = `${this.baseUrl}/feed/personalized`;
    debugLogger.logApiCall('GET', endpoint, { page, size });
    
    try {
      const params: any = {};
      if (page !== undefined) params.page = page;
      if (size !== undefined) params.size = size;

      const response = await apiClient.get<any>(endpoint, {
        params
      });
      
      debugLogger.logApiResponse('GET', endpoint, response);
      
      debugLogger.log('PostService', `🎯 Personalized Feed API Response`, {
        endpoint,
        page: page ?? 'all',
        size: size ?? 'all',
        totalPosts: response.content?.length || 0,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      });
      
      return response;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('PostService', `❌ Personalized Feed API Error`, {
        endpoint,
        page,
        size,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async toggleLike(postId: number): Promise<void> {
    try {
        const post = await this.getPost(postId);
        const currentUserReacted = post.reactions?.currentUserReacted;

        if (currentUserReacted) {
            await this.unreactPost(postId);
        } else {
            await this.reactToPost(postId, { type: 'like' });
        }
    } catch (error) {
        debugLogger.log('PostService', 'Error toggling like', { postId, error });
        throw error;
    }
}
}

export const postService = new PostService();
